import { Story } from '../../../../dorian-shared/types/story/Story';
import { User } from '../../../../dorian-shared/types/user/User';
import {
  BASIL_LIVE_DENYLIST,
  BASIL_WRITE_STORY_ALLOWLIST,
  BASIL_WRITE_USER_ID,
  BasilEnvironment,
  canBasilWrite,
  isStoryDraft,
} from '../../../utils/basilGate';
import { ApplyRefusal } from './writeTypes';

/**
 * W1.1 and W1.2 of the write contract (spec section 5.1), as a NAMED refusal.
 *
 * The boolean gate itself is `canBasilWrite` in `components/utils/basilGate.ts` (spec section 8.6
 * as amended by A1) — one reviewable place, owned by the mount lane. This module does not restate
 * the policy; it re-runs the same conditions only to name which one refused, and then asserts the
 * gate agrees. If the two ever disagree, the gate wins and the apply is refused (fail closed).
 *
 * This module must NOT call `isUserActionEnabled`: that returns `true` for every admin before it
 * examines actions (`userActionUtils.ts:9-18`), which is the admin bypass ruling D4 removes.
 * Negative test E9 asserts the call is absent from the whole write lane.
 */

export interface BasilWriteSubject {
  /** The authenticated user; `null` is always refused. */
  user: Pick<User, 'id'> | null | undefined;
  /** The episode being written to. */
  story: Pick<Story, 'id' | 'group'> | null | undefined;
  /** From `detectBasilEnvironment(api.defaults.baseURL)`; an unknown base URL reads `'live'`. */
  environment: BasilEnvironment;
  /** `isLiveEditDisabled(role, group)` from the page: read-only refuses before anything else. */
  isReadOnly: boolean;
}

/**
 * Returns the named refusal, or `null` when the subject may write.
 *
 * Order: read-only, then the denylist, then draft, then (on `'live'` only) owner and allowlist.
 * The denylist runs before the owner check so that a denylisted id is named `'live-denylist'` in
 * BOTH environments (negative test E24); every order refuses the same subjects, only the name of
 * the refusal differs, and the most specific name is the useful one.
 */
export function basilWriteRefusal(subject: BasilWriteSubject): ApplyRefusal | null {
  const {
    user, story, environment, isReadOnly,
  } = subject;

  if (isReadOnly) {
    return 'read-only';
  }

  if (!user) {
    return 'not-owner';
  }

  if (!story) {
    return 'story-not-allowlisted';
  }

  if (BASIL_LIVE_DENYLIST.includes(story.id)) {
    return 'live-denylist';
  }

  if (!isStoryDraft(story)) {
    return 'story-not-draft';
  }

  if (environment === 'live') {
    if (user.id !== BASIL_WRITE_USER_ID) {
      return 'not-owner';
    }

    if (!BASIL_WRITE_STORY_ALLOWLIST.includes(story.id)) {
      return 'story-not-allowlisted';
    }
  }

  // Belt and braces: the gate is the authority. A disagreement is a defect, and it fails closed.
  if (!canBasilWrite(user, story, environment)) {
    return 'not-owner';
  }

  return null;
}
